import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import {
  agentsApi,
  proposalsApi,
  runsApi,
  logsApi,
  healthApi,
  settingsApi,
  updatesApi,
  cacheApi,
  eventsApi,
  statsApi,
  type SportGroup
} from '@/services/api'
import {
  AgentFilters,
  ProposalFilters,
  RunFilters,
  LogFilters,
  CreateAgentForm,
  UpdateAgentForm,
  UpdateFilters
} from '@/types'

// Agents hooks
export const useAvailableAgents = () => {
  return useQuery({
    queryKey: ['agents', 'available'],
    queryFn: () => agentsApi.getAvailable(),
    staleTime: 300000, // 5 minutes - agent types don't change often
  })
}

export const useAgents = (filters: AgentFilters = {}) => {
  return useQuery({
    queryKey: ['agents', filters],
    queryFn: () => agentsApi.getAll(filters),
    staleTime: 30000, // 30 seconds
  })
}

export const useAgent = (id: string) => {
  return useQuery({
    queryKey: ['agents', id],
    queryFn: () => agentsApi.getById(id),
    enabled: !!id,
  })
}

export const useAgentStatus = (id: string) => {
  return useQuery({
    queryKey: ['agents', id, 'status'],
    queryFn: () => agentsApi.getStatus(id),
    enabled: !!id,
    refetchInterval: 10000, // Refresh every 10 seconds
  })
}

export const useAgentValidation = (id: string) => {
  return useQuery({
    queryKey: ['agents', id, 'validation'],
    queryFn: () => agentsApi.validate(id),
    enabled: !!id,
    staleTime: 30000, // 30 seconds
  })
}

export const useCreateAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: CreateAgentForm) => agentsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      enqueueSnackbar(response.message || 'Agent créé avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de la création de l'agent",
        { variant: 'error' }
      )
    },
  })
}

export const useUpdateAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentForm }) =>
      agentsApi.update(id, data),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      enqueueSnackbar(response.message || 'Agent mis à jour', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useToggleAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.toggle(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      enqueueSnackbar(response.message || "Statut de l'agent modifié", { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors du changement de statut',
        { variant: 'error' }
      )
    },
  })
}

export const useRunAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.run(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      // Invalider les propositions car l'agent va potentiellement en créer de nouvelles
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || "Exécution de l'agent démarrée", { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors du lancement de l'agent",
        { variant: 'error' }
      )
    },
  })
}

export const useReinstallAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.reinstall(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      enqueueSnackbar(response.message || 'Agent réinstallé avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la réinstallation',
        { variant: 'error' }
      )
    },
  })
}

export const useResetAgentCursor = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.resetCursor(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      queryClient.invalidateQueries({ queryKey: ['agents', id, 'state'] })
      enqueueSnackbar(response.message || 'État réinitialisé avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la réinitialisation',
        { variant: 'error' }
      )
    },
  })
}

export const useResetAgentCooldown = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.resetCooldown(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['agents', id] })
      queryClient.invalidateQueries({ queryKey: ['agents', id, 'state'] })
      enqueueSnackbar(response.message || 'Cooldown réinitialisé avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la réinitialisation du cooldown',
        { variant: 'error' }
      )
    },
  })
}

export const useAgentState = (id: string, key?: string) => {
  return useQuery({
    queryKey: ['agents', id, 'state', key],
    queryFn: () => agentsApi.getState(id, key),
    enabled: !!id,
    staleTime: 10000, // 10 secondes
    refetchInterval: 30000, // Rafraîchir toutes les 30s pour voir la progression
  })
}

export const useDeleteAgent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => agentsApi.delete(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      enqueueSnackbar(response.message || 'Agent supprimé', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la suppression',
        { variant: 'error' }
      )
    },
  })
}

// Proposals hooks
export const useProposals = (filters: ProposalFilters = {}, limit = 20, offset = 0, sort = 'created-desc') => {
  return useQuery({
    queryKey: ['proposals', filters, limit, offset, sort],
    queryFn: () => proposalsApi.getAll(filters, limit, offset, sort),
    staleTime: 60000, // ⏱️ 60s - éviter refetch inutiles au rafraîchissement
    gcTime: 300000, // 5 minutes (garde le cache plus longtemps)
    refetchInterval: 120000, // Auto-refresh toutes les 2 minutes
    refetchOnWindowFocus: false, // Désactiver pour éviter les refetch excessifs
    refetchOnMount: false, // ✅ Utiliser le cache si frais (< 60s)
    retry: 1, // Réessayer qu'une seule fois en cas d'échec
  })
}

export const useEligibleProposalsCount = () => {
  return useQuery({
    queryKey: ['proposals', 'eligible-count'],
    queryFn: () => proposalsApi.getEligibleCount(),
    staleTime: 30000, // 30 secondes
    refetchInterval: 60000, // Rafraîchir toutes les minutes
  })
}

export const useProposal = (id: string) => {
  return useQuery({
    queryKey: ['proposals', id],
    queryFn: () => proposalsApi.getById(id),
    enabled: !!id,
  })
}

export const useProposalGroup = (groupKey: string) => {
  return useQuery({
    queryKey: ['proposals', 'group', groupKey],
    queryFn: () => proposalsApi.getByGroup(groupKey),
    enabled: !!groupKey,
    staleTime: 60000, // 60 secondes
    gcTime: 300000, // 5 minutes
    refetchInterval: 120000, // Auto-refresh toutes les 2 minutes
    refetchOnWindowFocus: false, // Désactiver
    refetchOnMount: false, // Utiliser le cache
    retry: 1,
  })
}

export const useUpdateProposal = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: async ({
      id,
      proposalIds,  // 📦 MODE GROUPÉ
      primaryProposalId,  // ✅ Two-Panes: proposition prioritaire (pas de merge)
      status,
      reviewedBy,
      appliedChanges,
      userModifiedChanges,
      modificationReason,
      modifiedBy,
      block,
      killEvent,
      changes  // 📦 MODE GROUPÉ : payload consolidé
    }: {
      id?: string
      proposalIds?: string[]  // 📦 MODE GROUPÉ
      primaryProposalId?: string  // ✅ Two-Panes: proposition prioritaire
      status?: string
      reviewedBy?: string
      appliedChanges?: Record<string, any>
      userModifiedChanges?: Record<string, any>
      modificationReason?: string
      modifiedBy?: string
      block?: string
      killEvent?: boolean
      changes?: Record<string, any>  // 📦 MODE GROUPÉ
    }) => {
      // 📦 MODE GROUPÉ : Détecter et router vers le bon endpoint
      if (proposalIds && proposalIds.length > 0 && block) {
        console.log(`📦 useUpdateProposal MODE GROUPÉ: ${proposalIds.length} propositions, bloc "${block}", primaryProposalId: ${primaryProposalId || 'none'}`)
        return proposalsApi.validateBlockGroup(proposalIds, block, changes || {}, primaryProposalId)
      }

      // Mode simple (1 proposition) - retourner un tableau pour uniformiser le type
      if (!id) {
        throw new Error('id ou proposalIds requis')
      }
      const result = await proposalsApi.update(id, { status, reviewedBy, appliedChanges, userModifiedChanges, modificationReason, modifiedBy, block, killEvent })
      // ✅ Retourner un tableau pour uniformiser avec le mode groupé
      return { ...result, data: [result.data] }
    },
    onSuccess: (response, { id, proposalIds }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })

      // Invalider les propositions concernées
      if (proposalIds && proposalIds.length > 0) {
        proposalIds.forEach(proposalId => {
          queryClient.invalidateQueries({ queryKey: ['proposals', proposalId] })
        })
        // ✅ Invalider TOUS les groupes car on ne connait pas le groupKey ici
        queryClient.invalidateQueries({ queryKey: ['proposals', 'group'] })
        enqueueSnackbar(response.message || `${proposalIds.length} propositions mises à jour`, { variant: 'success' })
      } else if (id) {
        queryClient.invalidateQueries({ queryKey: ['proposals', id] })
        // ✅ Invalider les groupes car la proposition peut appartenir à un groupe
        queryClient.invalidateQueries({ queryKey: ['proposals', 'group'] })
        enqueueSnackbar(response.message || 'Proposition mise à jour', { variant: 'success' })
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useCompareProposal = () => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ id, existingEventId }: { id: string; existingEventId?: string }) =>
      proposalsApi.compare(id, existingEventId),
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la comparaison',
        { variant: 'error' }
      )
    },
  })
}

export const useBulkApproveProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ proposalIds, reviewedBy }: { proposalIds: string[]; reviewedBy?: string }) =>
      proposalsApi.bulkApprove(proposalIds, reviewedBy),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || `${response.data.updated} propositions approuvées`, { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de l'approbation en masse",
        { variant: 'error' }
      )
    },
  })
}

export const useBulkRejectProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ proposalIds, reviewedBy }: { proposalIds: string[]; reviewedBy?: string }) =>
      proposalsApi.bulkReject(proposalIds, reviewedBy),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || `${response.data.updated} propositions rejetées`, { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors du rejet en masse',
        { variant: 'error' }
      )
    },
  })
}

export const useBulkArchiveProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ proposalIds, reviewedBy, archiveReason }: {
      proposalIds: string[];
      reviewedBy?: string;
      archiveReason?: string
    }) =>
      proposalsApi.bulkArchive(proposalIds, reviewedBy, archiveReason),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || `${response.data.updated} propositions archivées`, { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de l'archivage en masse",
        { variant: 'error' }
      )
    },
  })
}

export const useUnapproveProposal = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => proposalsApi.unapprove(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals', id] })
      enqueueSnackbar(response.message || 'Approbation annulée', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de l'annulation de l'approbation",
        { variant: 'error' }
      )
    },
  })
}

export const useUnapproveBlock = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ id, block }: { id: string; block: string }) => proposalsApi.unapproveBlock(id, block),
    onSuccess: (response, { id, block }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals', id] })
      queryClient.invalidateQueries({ queryKey: ['proposals', 'group'] })
      enqueueSnackbar(response.message || `Bloc "${block}" annulé`, { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de l'annulation du bloc",
        { variant: 'error' }
      )
    },
  })
}

export const useApplyPendingProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (proposalIds: string[]) => proposalsApi.applyPending(proposalIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      const data = response.data
      if (data.failed === 0) {
        enqueueSnackbar(`${data.success} changement(s) appliqué(s) en base`, { variant: 'success' })
      } else {
        enqueueSnackbar(`${data.success} succès, ${data.failed} échec(s)`, { variant: 'warning' })
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de l'application des changements",
        { variant: 'error' }
      )
    },
  })
}

export const useValidateAndApplyProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: { proposalIds: string[]; changes: Record<string, any>; primaryProposalId?: string; apply?: boolean; blocks?: string[] }) =>
      proposalsApi.validateAndApply(data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      const data = response.data
      const didApply = variables.apply !== false
      if (didApply) {
        if (data.failed === 0) {
          enqueueSnackbar(`${data.blocksValidated.length} bloc(s) validé(s) et appliqué(s) en base`, { variant: 'success' })
        } else {
          enqueueSnackbar(`${data.success} succès, ${data.failed} échec(s)`, { variant: 'warning' })
        }
      } else {
        enqueueSnackbar(`${data.blocksValidated.length} bloc(s) validé(s)`, { variant: 'success' })
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la validation',
        { variant: 'error' }
      )
    },
  })
}

export const useConvertToEditionUpdate = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: {
      proposalId: string
      eventId: number
      editionId: number
      eventName: string
      eventSlug: string
      editionYear: string
    }) => proposalsApi.convertToEditionUpdate(data.proposalId, data),
    onSuccess: (response, { eventName }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(
        response.message || `Proposition convertie en EDITION_UPDATE pour ${eventName}`,
        { variant: 'success' }
      )
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la conversion de la proposition',
        { variant: 'error' }
      )
    },
  })
}

export const useDuplicateEdition = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: { proposalId: string; eventId: number; targetYear: number }) =>
      proposalsApi.duplicateEdition(data.eventId, data.targetYear),
    onSuccess: (response, { proposalId, eventId, targetYear }) => {
      // L'endpoint crée ET applique immédiatement la duplication → l'édition cible existe.
      // MAIS on ne peut PAS refetch check-existing-event : le matching (matchEvent/Meilisearch)
      // ne voit pas une édition fraîchement créée (index Meilisearch en retard + fenêtre de dates).
      // On patche donc directement le cache avec le newEditionId renvoyé par l'endpoint
      // (source de vérité) → « Sélectionner » s'active, message + bouton disparaissent.
      const newEditionId = response.data?.newEditionId
      if (newEditionId != null) {
        queryClient.setQueryData(['proposal-check-existing', proposalId], (old: any) => {
          if (!old) return old
          const patch = (m: any) =>
            m && m.eventId === eventId
              ? { ...m, editionId: newEditionId, editionYear: String(targetYear) }
              : m
          return {
            ...old,
            match: patch(old.match),
            matches: Array.isArray(old.matches) ? old.matches.map(patch) : old.matches,
          }
        })
      } else {
        // Pas d'id renvoyé (cas dégradé) : on retombe sur un refetch best-effort.
        queryClient.invalidateQueries({ queryKey: ['proposal-check-existing', proposalId] })
      }
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(
        response.message || `Édition ${targetYear} créée par duplication`,
        { variant: 'success' }
      )
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || "Erreur lors de la duplication de l'édition",
        { variant: 'error' }
      )
    },
  })
}

export const useCheckExistingEvent = (proposalId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['proposal-check-existing', proposalId],
    queryFn: () => proposalsApi.checkExistingEvent(proposalId),
    enabled,
    staleTime: 5 * 60 * 1000, // Cache 5 min
    refetchOnMount: true
  })
}

// EDITION_UPDATE : rejectedMatches enrichis (editionId résolu par lookup direct côté API)
export const useCheckAlternativeEditions = (proposalId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['proposal-check-alternative-editions', proposalId],
    queryFn: () => proposalsApi.checkAlternativeEditions(proposalId),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true
  })
}

export const useChangeTarget = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: {
      proposalId: string
      eventId: number
      editionId: number
      eventName: string
      eventSlug: string
      editionYear: string
    }) => proposalsApi.changeTarget(data.proposalId, data),
    onSuccess: (response, { eventName }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposal'] })
      enqueueSnackbar(
        response.message || `Cible modifiée : ${eventName}`,
        { variant: 'success' }
      )
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors du changement de cible',
        { variant: 'error' }
      )
    },
  })
}

export const useLinkProposalToEdition = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: async ({ proposalId, eventId, editionId }: { proposalId: string; eventId: number; editionId: number }) => {
      return proposalsApi.linkToEdition(proposalId, eventId, editionId)
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['proposals', variables.proposalId] })
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || 'Proposition liée avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la liaison',
        { variant: 'error' }
      )
    },
  })
}

export const useCreateManualProposal = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: {
      eventId?: string
      editionId?: string
      raceId?: string
      fieldName: string
      fieldValue: any
      type: 'NEW_EVENT' | 'EVENT_UPDATE' | 'EDITION_UPDATE' | 'RACE_UPDATE'
      propagateToRaces?: boolean
      justification?: string
    }) => proposalsApi.createManual(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || 'Proposition créée manuellement avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la création de la proposition',
        { variant: 'error' }
      )
    },
  })
}

export const useDeleteProposal = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => proposalsApi.delete(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || 'Proposition supprimée définitivement', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la suppression',
        { variant: 'error' }
      )
    },
  })
}

export const useBulkDeleteProposals = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ proposalIds, reviewedBy }: {
      proposalIds: string[];
      reviewedBy?: string;
    }) =>
      proposalsApi.bulkDelete(proposalIds, reviewedBy),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || `${response.data.deleted} propositions supprimées définitivement`, { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la suppression en masse',
        { variant: 'error' }
      )
    },
  })
}

// Runs hooks
export const useRuns = (filters: RunFilters = {}, limit = 20, offset = 0) => {
  return useQuery({
    queryKey: ['runs', filters, limit, offset],
    queryFn: () => runsApi.getAll(filters, limit, offset),
    staleTime: 30000,
  })
}

export const useRun = (id: string) => {
  return useQuery({
    queryKey: ['runs', id],
    queryFn: () => runsApi.getById(id),
    enabled: !!id,
  })
}

// Logs hooks
export const useLogs = (filters: LogFilters = {}, limit = 100, offset = 0) => {
  return useQuery({
    queryKey: ['logs', filters, limit, offset],
    queryFn: () => logsApi.getAll(filters, limit, offset),
    staleTime: 10000, // Logs are more dynamic
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })
}

// Settings hooks
export const useSettings = () => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 300000, // 5 minutes - settings don't change often
  })
}

export const useUpdateSettings = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: settingsApi.update,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      enqueueSnackbar(response.message || 'Paramètres mis à jour avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Erreur lors de la mise à jour des paramètres',
        { variant: 'error' }
      )
    },
  })
}

export const useFailureReport = () => {
  return useQuery({
    queryKey: ['settings', 'failure-report'],
    queryFn: () => settingsApi.getFailureReport(),
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // Auto-refresh every 5 minutes
  })
}

export const useCheckFailures = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: settingsApi.checkFailures,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'failure-report'] })
      queryClient.invalidateQueries({ queryKey: ['agents'] })

      const { disabledAgents, checkedAgents } = response.data
      if (disabledAgents.length > 0) {
        enqueueSnackbar(
          `Vérification terminée : ${disabledAgents.length} agents désactivés sur ${checkedAgents} vérifiés`,
          { variant: 'warning' }
        )
      } else {
        enqueueSnackbar(
          `Vérification terminée : ${checkedAgents} agents vérifiés, aucune désactivation nécessaire`,
          { variant: 'success' }
        )
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Erreur lors de la vérification des échecs',
        { variant: 'error' }
      )
    },
  })
}

export const useAgentFailures = (agentId: string) => {
  return useQuery({
    queryKey: ['settings', 'agent-failures', agentId],
    queryFn: () => settingsApi.getAgentFailures(agentId),
    enabled: !!agentId,
    staleTime: 30000, // 30 seconds
  })
}

export const useAutoApplyStatus = () => {
  return useQuery({
    queryKey: ['settings', 'auto-apply-status'],
    queryFn: () => settingsApi.getAutoApplyStatus(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Auto-refresh every minute
  })
}

export const useRunAutoApply = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: settingsApi.runAutoApply,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'auto-apply-status'] })
      queryClient.invalidateQueries({ queryKey: ['updates'] })

      const { success, failed } = response.data
      if (failed > 0) {
        enqueueSnackbar(
          `Application terminée : ${success} réussie(s), ${failed} échec(s)`,
          { variant: 'warning' }
        )
      } else if (success > 0) {
        enqueueSnackbar(
          `${success} mise(s) à jour appliquée(s) avec succès`,
          { variant: 'success' }
        )
      } else {
        enqueueSnackbar(
          'Aucune mise à jour en attente à appliquer',
          { variant: 'info' }
        )
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Erreur lors de l\'application automatique',
        { variant: 'error' }
      )
    },
  })
}

// Health hook
export const useHealth = () => {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => healthApi.check(),
    refetchInterval: 60000, // Check every minute
    retry: false,
  })
}

// Updates hooks
export const useUpdates = (filters: UpdateFilters = {}, limit = 20, offset = 0) => {
  return useQuery({
    queryKey: ['updates', filters, limit, offset],
    queryFn: () => updatesApi.getAll(filters, limit, offset),
    staleTime: 30000,
  })
}

export const useUpdate = (id: string) => {
  return useQuery({
    queryKey: ['updates', id],
    queryFn: () => updatesApi.getById(id),
    enabled: !!id,
  })
}

export const useCreateUpdate = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ proposalId, scheduledAt }: { proposalId: string; scheduledAt?: string }) =>
      updatesApi.create(proposalId, scheduledAt),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      enqueueSnackbar(response.message || 'Mise à jour créée', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la création de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useApplyUpdate = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => updatesApi.apply(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      queryClient.invalidateQueries({ queryKey: ['updates', id] })
      enqueueSnackbar(response.message || 'Mise à jour appliquée', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de l\'application de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useDeleteUpdate = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => updatesApi.delete(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      enqueueSnackbar(response.message || 'Mise à jour supprimée', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la suppression de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useReplayUpdate = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => updatesApi.replay(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      queryClient.invalidateQueries({ queryKey: ['updates', id] })
      enqueueSnackbar(response.message || 'Mise à jour prête pour rejeu', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors du rejeu de la mise à jour',
        { variant: 'error' }
      )
    },
  })
}

export const useUpdateLogs = (id: string) => {
  return useQuery({
    queryKey: ['updates', id, 'logs'],
    queryFn: () => updatesApi.getLogs(id),
    enabled: !!id,
    refetchInterval: 5000, // Auto-refresh logs every 5 seconds
  })
}

export const useBulkDeleteUpdates = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (ids: string[]) => updatesApi.bulkDelete(ids),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      enqueueSnackbar(
        response.message || `${response.data.deletedCount} mise(s) à jour supprimée(s)`,
        { variant: 'success' }
      )
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la suppression en masse',
        { variant: 'error' }
      )
    },
  })
}

export const useBulkApplyUpdates = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (ids: string[]) => updatesApi.bulkApply(ids),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['updates'] })
      const { successful, failed } = response.data
      if (failed.length === 0) {
        enqueueSnackbar(
          response.message || `${successful.length} mise(s) à jour appliquée(s)`,
          { variant: 'success' }
        )
      } else {
        enqueueSnackbar(
          `${successful.length} appliquée(s), ${failed.length} échec(s)`,
          { variant: 'warning' }
        )
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de l\'application en masse',
        { variant: 'error' }
      )
    },
  })
}

// Cache hooks
export const useEvents = (filters: { limit?: number; search?: string } = {}) => {
  return useQuery({
    queryKey: ['cache', 'events', filters],
    queryFn: () => cacheApi.getEvents(filters),
    staleTime: 300000, // 5 minutes - events don't change often
  })
}

export const useEditions = (filters: { eventId?: string; limit?: number } = {}) => {
  return useQuery({
    queryKey: ['cache', 'editions', filters],
    queryFn: () => cacheApi.getEditions(filters),
    staleTime: 300000, // 5 minutes
    enabled: !!filters.eventId, // Only run if eventId is provided
  })
}

export const useRaces = (filters: { editionId?: string; limit?: number } = {}) => {
  return useQuery({
    queryKey: ['cache', 'races', filters],
    queryFn: () => cacheApi.getRaces(filters),
    staleTime: 300000, // 5 minutes
    enabled: !!filters.editionId, // Only run if editionId is provided
  })
}

// Events hooks (kill/revive)
export const useKillEvent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (eventId: string) => eventsApi.kill(eventId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      enqueueSnackbar(response.message || 'Événement tué avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la mise à mort de l\'événement',
        { variant: 'error' }
      )
    },
  })
}

export const useReviveEvent = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: ({ eventId, editionId }: { eventId: string; editionId?: string }) =>
      eventsApi.revive(eventId, editionId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposal-groups'] })
      enqueueSnackbar(response.message || 'Événement ressuscité avec succès', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.error?.message || 'Erreur lors de la résurrection de l\'événement',
        { variant: 'error' }
      )
    },
  })
}

// Miles Republic direct access hooks
export const useMilesRepublicEvents = (filters: { limit?: number; search?: string } = {}) => {
  return useQuery({
    queryKey: ['miles-republic', 'events', filters],
    queryFn: () => cacheApi.getMilesRepublicEvents(filters),
    staleTime: 60000, // 1 minute - real-time data
  })
}

export const useMilesRepublicEditions = (filters: { eventId?: string; limit?: number } = {}) => {
  return useQuery({
    queryKey: ['miles-republic', 'editions', filters],
    queryFn: () => cacheApi.getMilesRepublicEditions(filters),
    staleTime: 60000, // 1 minute
    enabled: !!filters.eventId, // Only enable when eventId is provided
  })
}

export const useMilesRepublicRaces = (filters: { editionId?: string; limit?: number } = {}) => {
  return useQuery({
    queryKey: ['miles-republic', 'races', filters],
    queryFn: () => cacheApi.getMilesRepublicRaces(filters),
    staleTime: 60000, // 1 minute
    enabled: !!filters.editionId, // Only enable when editionId is provided
  })
}

// Stats hooks
export const useCalendarConfirmations = (filters: {
  startDate?: string
  endDate?: string
  granularity?: 'day' | 'week' | 'month' | 'quarter' | 'year'
  sport?: SportGroup
} = {}) => {
  return useQuery({
    queryKey: ['stats', 'calendar-confirmations', filters],
    queryFn: () => statsApi.getCalendarConfirmations(filters),
    staleTime: 300000,
  })
}

export const usePendingConfirmations = (filters: {
  startDate?: string
  endDate?: string
  granularity?: 'day' | 'week' | 'month' | 'quarter' | 'year'
  sport?: SportGroup
} = {}) => {
  return useQuery({
    queryKey: ['stats', 'pending-confirmations', filters],
    queryFn: () => statsApi.getPendingConfirmations(filters),
    staleTime: 300000,
  })
}

export const useProposalsCreated = (filters: {
  startDate?: string
  endDate?: string
  granularity?: 'day' | 'week' | 'month' | 'quarter' | 'year'
} = {}) => {
  return useQuery({
    queryKey: ['stats', 'proposals-created', filters],
    queryFn: () => statsApi.getProposalsCreated(filters),
    staleTime: 300000,
  })
}

export const useUserLeaderboard = (filters: {
  startDate?: string
  endDate?: string
} = {}) => {
  return useQuery({
    queryKey: ['stats', 'user-leaderboard', filters],
    queryFn: () => statsApi.getUserLeaderboard(filters),
    staleTime: 300000,
  })
}

export const useConfirmationRateBySport = () => {
  return useQuery({
    queryKey: ['stats', 'confirmation-rate-by-sport'],
    queryFn: () => statsApi.getConfirmationRateBySport(),
    staleTime: 300000,
  })
}

// ============================================================================
// Event Merge hooks
// ============================================================================

/**
 * Hook pour récupérer les détails d'un événement depuis Miles Republic
 */
export const useEventDetails = (eventId: number | null) => {
  return useQuery({
    queryKey: ['event-details', eventId],
    queryFn: async () => {
      if (!eventId) return null
      const response = await eventsApi.getDetails(eventId.toString())
      return response.data?.event || null
    },
    enabled: !!eventId,
    staleTime: 60000, // 1 minute
  })
}

/**
 * Hook pour créer une proposition de fusion d'événements
 */
export const useCreateMergeProposal = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (data: {
      keepEventId: number
      duplicateEventId: number
      newEventName?: string
      reason?: string
      forceOverwrite?: boolean
      copyMissingEditions?: boolean
    }) => proposalsApi.createMerge(data),
    onSuccess: (response) => {
      enqueueSnackbar(response.message || 'Proposition de fusion créée', { variant: 'success' })
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.message || 'Erreur lors de la création'
      enqueueSnackbar(message, { variant: 'error' })
    }
  })
}

/**
 * Hook pour inverser le sens de fusion d'une proposition EVENT_MERGE
 */
export const useSwapMergeDirection = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: (id: string) => proposalsApi.swapMergeDirection(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals', id] })
      enqueueSnackbar(response.message || 'Sens de fusion inversé', { variant: 'success' })
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.message || 'Erreur lors de l\'inversion'
      enqueueSnackbar(message, { variant: 'error' })
    }
  })
}
